import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { ClientSubscription, SubscriptionPlan, SubscriptionStatus, BillingCycle } from '../types/subscription';
import { Filter, RefreshCw, X, RotateCcw, Plus, Pencil, Ban, CheckCircle2, Package, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ALLOWED_ROLES = ['CEO', 'GROUP_MANAGER', 'MANAGER', 'EXECUTIVE'] as const;

const getDaysRemaining = (endDate: string): number =>
  Math.ceil((new Date(endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const formatPrice = (paise: number): string =>
  `₹${(paise / 100).toLocaleString('en-IN')}`;

const getNextDay = (dateStr: string): string => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

// ---------- Status Indicator Component (Task 3) ----------

const StatusIndicator: React.FC<{ status: SubscriptionStatus; daysLeft: number }> = ({ status, daysLeft }) => {
  if (status === 'CANCELLED') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 line-through">
        Cancelled
      </span>
    );
  }
  if (status === 'PAUSED') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        Paused
      </span>
    );
  }
  if (status === 'EXPIRED' || daysLeft <= 0) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Expired
      </span>
    );
  }
  if (daysLeft <= 7) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
        </span>
        {daysLeft}d left
      </span>
    );
  }
  if (daysLeft <= 30) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700">
        <span className="relative flex h-2 w-2">
          <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
        </span>
        {daysLeft}d left
      </span>
    );
  }
  // ACTIVE with plenty of time
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
      <span className="relative flex h-2 w-2">
        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
      </span>
      {daysLeft}d left
    </span>
  );
};

// ---------- Auto-Renew Toggle Component (Task 2) ----------

const AutoRenewToggle: React.FC<{
  subscriptionId: string;
  autoRenew: boolean;
  status: SubscriptionStatus;
  onToggled: () => void;
}> = ({ subscriptionId, autoRenew, status, onToggled }) => {
  const [toggling, setToggling] = useState(false);
  const [localValue, setLocalValue] = useState(autoRenew);

  if (status !== 'ACTIVE') {
    return <span className="text-xs text-gray-400">--</span>;
  }

  const handleToggle = async () => {
    setToggling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscriptions/${subscriptionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ autoRenew: !localValue }),
      });
      if (!response.ok) throw new Error('Failed to update auto-renew');
      setLocalValue(!localValue);
      onToggled();
    } catch (err) {
      alert('Failed to toggle auto-renew. Please try again.');
    } finally {
      setToggling(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={toggling}
      className="inline-flex items-center gap-1.5 group"
      title={localValue ? 'Auto-renew is ON — click to disable' : 'Auto-renew is OFF — click to enable'}
    >
      <div
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          localValue ? 'bg-green-500' : 'bg-gray-300'
        } ${toggling ? 'opacity-50' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
            localValue ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </div>
      {localValue && (
        <span className="text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
          Auto
        </span>
      )}
    </button>
  );
};

// ---------- Renew Subscription Modal (Task 1) ----------

const RenewSubscriptionModal: React.FC<{
  isOpen: boolean;
  subscription: ClientSubscription | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ isOpen, subscription, onClose, onSuccess }) => {
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data: plans = [], isLoading: plansLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch plans');
      const result = await response.json();
      return (result.data || []).filter((p: SubscriptionPlan) => p.isActive);
    },
    enabled: isOpen,
  });

  // Initialize defaults when modal opens with a subscription
  React.useEffect(() => {
    if (isOpen && subscription) {
      setSelectedPlanId(subscription.planId || '');
      setStartDate(getNextDay(subscription.endDate));
      setSubmitting(false);
    }
  }, [isOpen, subscription]);

  if (!isOpen || !subscription) return null;

  const handleConfirm = async () => {
    if (!selectedPlanId) {
      alert('Please select a subscription plan.');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          leadId: subscription.leadId,
          planId: selectedPlanId,
          startDate: new Date(startDate).toISOString(),
          autoRenew: subscription.autoRenew,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to create renewal subscription');
      }
      alert('Subscription renewed successfully.');
      onSuccess();
      onClose();
    } catch (err: any) {
      alert(err.message || 'Failed to renew subscription. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <RotateCcw className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Renew Subscription</h2>
            <p className="text-sm text-gray-500">{subscription.lead?.name ?? 'Unknown client'}</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Current plan: <strong>{subscription.planName}</strong> (expires {formatDate(subscription.endDate)})
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Plan
            </label>
            {plansLoading ? (
              <div className="text-sm text-gray-500">Loading plans...</div>
            ) : (
              <select
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a plan</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} — ₹{(plan.price / 100).toLocaleString('en-IN')}/{plan.billingCycle === 'MONTHLY' ? 'mo' : plan.billingCycle === 'QUARTERLY' ? 'qtr' : 'yr'}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedPlanId || submitting}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            {submitting ? 'Renewing...' : 'Confirm Renewal'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Create Subscription Modal ----------

interface WonLead {
  id: string;
  name: string;
  company?: string;
  email?: string;
}

const CreateSubscriptionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ isOpen, onClose, onSuccess }) => {
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);

  const { data: wonLeads = [], isLoading: leadsLoading } = useQuery<WonLead[]>({
    queryKey: ['won-leads'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/leads?stage=WON`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch WON leads');
      const result = await response.json();
      return (result.data || result.leads || []).map((l: any) => ({
        id: l.id,
        name: l.name,
        company: l.company,
        email: l.email,
      }));
    },
    enabled: isOpen,
  });

  const { data: plans = [], isLoading: plansLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch plans');
      const result = await response.json();
      return (result.data || []).filter((p: SubscriptionPlan) => p.isActive);
    },
    enabled: isOpen,
  });

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedLeadId('');
      setSelectedPlanId('');
      setStartDate(new Date().toISOString().split('T')[0]);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedLeadId) {
      alert('Please select a WON lead.');
      return;
    }
    if (!selectedPlanId) {
      alert('Please select a subscription plan.');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');

      // Create the subscription. The backend auto-creates the plan's
      // deliverables as part of this call — do NOT also POST to
      // /stage-gates/auto-create-deliverables or they will be duplicated.
      const subResponse = await fetch(`${API_URL}/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          leadId: selectedLeadId,
          planId: selectedPlanId,
          startDate: new Date(startDate).toISOString(),
        }),
      });
      if (!subResponse.ok) {
        const err = await subResponse.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to create subscription');
      }

      alert('Subscription created successfully.');
      onSuccess();
      onClose();
    } catch (err: any) {
      alert(err.message || 'Failed to create subscription. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLead = wonLeads.find((l) => l.id === selectedLeadId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Plus className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Create Subscription</h2>
            <p className="text-sm text-gray-500">Assign a plan to a WON lead</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Lead selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              WON Lead
            </label>
            {leadsLoading ? (
              <div className="text-sm text-gray-500">Loading leads...</div>
            ) : wonLeads.length === 0 ? (
              <div className="text-sm text-gray-500">No WON leads found.</div>
            ) : (
              <select
                value={selectedLeadId}
                onChange={(e) => setSelectedLeadId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a WON lead</option>
                {wonLeads.map((lead) => (
                  <option key={lead.id} value={lead.id}>
                    {lead.name}{lead.company ? ` — ${lead.company}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Plan selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Plan
            </label>
            {plansLoading ? (
              <div className="text-sm text-gray-500">Loading plans...</div>
            ) : plans.length === 0 ? (
              <div className="text-sm text-gray-500">No active plans found. Create plans in Manage Plans.</div>
            ) : (
              <select
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a plan</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} — ₹{(plan.price / 100).toLocaleString('en-IN')}/{plan.billingCycle === 'MONTHLY' ? 'mo' : plan.billingCycle === 'QUARTERLY' ? 'qtr' : 'yr'}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Start date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Summary */}
          {selectedLeadId && selectedPlanId && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs font-medium text-blue-700 mb-2">What happens next</p>
              <ul className="space-y-1.5 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5">1.</span>
                  Subscription will be created for <span className="font-medium">{selectedLead?.name}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5">2.</span>
                  Deliverables will be auto-generated from the plan template
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedLeadId || !selectedPlanId || submitting}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            {submitting ? 'Creating...' : 'Create Subscription'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Plan Form Modal (Create / Edit) ----------

interface PlanFormData {
  name: string;
  price: string; // in rupees for display
  billingCycle: BillingCycle;
  durationMonths: string;
  description: string;
  features: string;
}

const emptyPlanForm: PlanFormData = {
  name: '',
  price: '',
  billingCycle: 'MONTHLY',
  durationMonths: '',
  description: '',
  features: '',
};

const PlanFormModal: React.FC<{
  isOpen: boolean;
  plan: SubscriptionPlan | null; // null = create mode
  onClose: () => void;
  onSuccess: () => void;
}> = ({ isOpen, plan, onClose, onSuccess }) => {
  const [form, setForm] = useState<PlanFormData>(emptyPlanForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (isOpen) {
      setError('');
      setSubmitting(false);
      if (plan) {
        setForm({
          name: plan.name,
          price: String(plan.price / 100),
          billingCycle: plan.billingCycle,
          durationMonths: String(plan.durationMonths),
          description: plan.description || '',
          features: (plan.features || []).join(', '),
        });
      } else {
        setForm(emptyPlanForm);
      }
    }
  }, [isOpen, plan]);

  if (!isOpen) return null;

  const isEdit = plan !== null;

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Plan name is required.'); return; }
    const priceNum = parseFloat(form.price);
    if (isNaN(priceNum) || priceNum <= 0) { setError('Price must be a positive number.'); return; }
    const durationNum = parseInt(form.durationMonths, 10);
    if (isNaN(durationNum) || durationNum <= 0) { setError('Duration must be a positive number.'); return; }

    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const featuresArray = form.features
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean);

      const body = {
        name: form.name.trim(),
        price: Math.round(priceNum * 100), // convert rupees to paise
        billingCycle: form.billingCycle,
        durationMonths: durationNum,
        description: form.description.trim() || undefined,
        features: featuresArray.length > 0 ? featuresArray : undefined,
      };

      const url = isEdit ? `${API_URL}/subscription-plans/${plan.id}` : `${API_URL}/subscription-plans`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Failed to ${isEdit ? 'update' : 'create'} plan`);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field: keyof PlanFormData, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${isEdit ? 'bg-blue-100' : 'bg-green-100'}`}>
            {isEdit ? <Pencil className="w-5 h-5 text-blue-600" /> : <Plus className="w-5 h-5 text-green-600" />}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Plan' : 'Create Plan'}</h2>
            <p className="text-sm text-gray-500">{isEdit ? 'Update plan details' : 'Add a new subscription plan'}</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g. Gold Monthly"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Price + Billing Cycle */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (in Rs.) *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">&#8377;</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => updateField('price', e.target.value)}
                  placeholder="5000"
                  className="w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle *</label>
              <select
                value={form.billingCycle}
                onChange={(e) => updateField('billingCycle', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="ANNUAL">Annual</option>
              </select>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration (months) *</label>
            <input
              type="number"
              min="1"
              value={form.durationMonths}
              onChange={(e) => updateField('durationMonths', e.target.value)}
              placeholder="12"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={2}
              placeholder="Brief description of the plan"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>

          {/* Features */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Features (comma-separated)</label>
            <textarea
              value={form.features}
              onChange={(e) => updateField('features', e.target.value)}
              rows={2}
              placeholder="Social media management, Content creation, Monthly reports"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
            {form.features.trim() && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.features.split(',').map((f, i) => f.trim() && (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                    {f.trim()}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ${
              isEdit ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {submitting ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Plan')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Plans Tab Content ----------

const PlansTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const { data: allPlans = [], isLoading, refetch } = useQuery<SubscriptionPlan[]>({
    queryKey: ['subscription-plans-all'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch plans');
      const result = await response.json();
      return result.data || [];
    },
  });

  const handleToggleActive = async (plan: SubscriptionPlan) => {
    setTogglingId(plan.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscription-plans/${plan.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isActive: !plan.isActive }),
      });
      if (!response.ok) throw new Error('Failed to update plan status');
      refetch();
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
    } catch {
      alert('Failed to update plan status. Please try again.');
    } finally {
      setTogglingId(null);
    }
  };

  const handlePlanSuccess = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
  };

  const openEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setShowPlanModal(true);
  };

  const openCreate = () => {
    setEditingPlan(null);
    setShowPlanModal(true);
  };

  const cycleSuffix = (cycle: BillingCycle) =>
    cycle === 'MONTHLY' ? '/mo' : cycle === 'QUARTERLY' ? '/qtr' : '/yr';

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{allPlans.length} plan{allPlans.length !== 1 ? 's' : ''}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Plan
          </button>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading plans...</div>
      ) : allPlans.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">No subscription plans yet.</p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Your First Plan
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Plan Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Price</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Billing Cycle</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Duration</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Features</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {allPlans.map((plan) => (
                <tr key={plan.id} className={`hover:bg-gray-50 ${!plan.isActive ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{plan.name}</div>
                    {plan.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{plan.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-900 font-medium">
                    {formatPrice(plan.price)}
                    <span className="text-xs text-gray-500 font-normal ml-0.5">{cycleSuffix(plan.billingCycle)}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 capitalize">{plan.billingCycle.toLowerCase()}</td>
                  <td className="px-4 py-3 text-gray-700">{plan.durationMonths} month{plan.durationMonths !== 1 ? 's' : ''}</td>
                  <td className="px-4 py-3">
                    {plan.features && plan.features.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {plan.features.slice(0, 3).map((f, i) => (
                          <span key={i} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                            {f}
                          </span>
                        ))}
                        {plan.features.length > 3 && (
                          <span className="text-xs text-gray-400">+{plan.features.length - 3} more</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">--</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {plan.isActive ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => openEdit(plan)}
                        className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Edit plan"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(plan)}
                        disabled={togglingId === plan.id}
                        className={`inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          plan.isActive
                            ? 'text-orange-700 bg-orange-50 hover:bg-orange-100'
                            : 'text-green-700 bg-green-50 hover:bg-green-100'
                        } ${togglingId === plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={plan.isActive ? 'Deactivate plan' : 'Activate plan'}
                      >
                        {plan.isActive ? (
                          <><Ban className="w-3.5 h-3.5" /> Deactivate</>
                        ) : (
                          <><CheckCircle2 className="w-3.5 h-3.5" /> Activate</>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PlanFormModal
        isOpen={showPlanModal}
        plan={editingPlan}
        onClose={() => { setShowPlanModal(false); setEditingPlan(null); }}
        onSuccess={handlePlanSuccess}
      />
    </>
  );
};

// ---------- Main Page ----------

export const SubscriptionsPage: React.FC = () => {
  const { user } = useAuth();
  const canManagePlans = user?.role !== 'EXECUTIVE';
  const [activeTab, setActiveTab] = useState<'subscriptions' | 'plans'>('subscriptions');
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | ''>('');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [renewTarget, setRenewTarget] = useState<ClientSubscription | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const queryParams = new URLSearchParams();
  if (statusFilter) queryParams.set('status', statusFilter);

  const { data: subscriptions = [], isLoading, refetch } = useQuery<ClientSubscription[]>({
    queryKey: ['subscriptions', statusFilter],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscriptions?${queryParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch subscriptions');
      const result = await response.json();
      return result.data || [];
    },
  });

  // Only CEO/GM/Manager can access this page
  if (user && !ALLOWED_ROLES.includes(user.role as typeof ALLOWED_ROLES[number])) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">You do not have permission to view this page.</p>
        </div>
      </DashboardLayout>
    );
  }

  const filtered = expiringOnly
    ? subscriptions.filter((s) => {
        const days = getDaysRemaining(s.endDate);
        return days <= 30 && days > 0;
      })
    : subscriptions;

  const canRenew = (sub: ClientSubscription): boolean => {
    const days = getDaysRemaining(sub.endDate);
    return sub.status === 'EXPIRED' || (sub.status === 'ACTIVE' && days <= 30);
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
          <p className="text-sm text-gray-500 mt-1">Manage client subscriptions and plans</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('subscriptions')}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'subscriptions'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Subscriptions
          </button>
          {canManagePlans && (
            <button
              onClick={() => setActiveTab('plans')}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'plans'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Plans
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'plans' && canManagePlans ? (
          <PlansTab />
        ) : (
          <>
            {/* Executive info banner */}
            {user?.role === 'EXECUTIVE' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-700">
                <Info className="h-4 w-4 inline mr-1" /> Subscription plans are managed by your manager. Contact them to create or modify plans.
              </div>
            )}

            {/* Subscriptions Header */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">
                {filtered.length} client subscription{filtered.length !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Subscription
                </button>
                <button
                  onClick={() => refetch()}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as SubscriptionStatus | '')}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="EXPIRED">Expired</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="PAUSED">Paused</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={expiringOnly}
                  onChange={(e) => setExpiringOnly(e.target.checked)}
                  className="rounded"
                />
                Expiring within 30 days
              </label>
            </div>

            {/* Table */}
            {isLoading ? (
              <div className="text-center py-12 text-gray-500">Loading subscriptions...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">No subscriptions yet</h3>
                <p className="text-sm text-gray-500 mb-4">Create a subscription to link a plan to a WON lead and auto-generate deliverables.</p>
                <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
                  Create Your First Subscription
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Client</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Plan</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Price</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Start</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Expiry</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Auto-Renew</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((sub) => {
                      const days = getDaysRemaining(sub.endDate);
                      return (
                        <tr key={sub.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{sub.lead?.name ?? '—'}</div>
                            <div className="text-xs text-gray-500">{sub.lead?.company ?? ''}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-900">{sub.planName}</td>
                          <td className="px-4 py-3 text-gray-900">
                            {formatPrice(sub.planPrice)}
                            <span className="text-xs text-gray-500 ml-1">
                              /{sub.billingCycle === 'MONTHLY' ? 'mo' : sub.billingCycle === 'QUARTERLY' ? 'qtr' : 'yr'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{formatDate(sub.startDate)}</td>
                          <td className="px-4 py-3 text-gray-600">{formatDate(sub.endDate)}</td>
                          <td className="px-4 py-3">
                            <StatusIndicator status={sub.status} daysLeft={days} />
                          </td>
                          <td className="px-4 py-3">
                            <AutoRenewToggle
                              subscriptionId={sub.id}
                              autoRenew={sub.autoRenew}
                              status={sub.status}
                              onToggled={() => refetch()}
                            />
                          </td>
                          <td className="px-4 py-3">
                            {canRenew(sub) && (
                              <button
                                onClick={() => setRenewTarget(sub)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Renew
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Renew Modal */}
      <RenewSubscriptionModal
        isOpen={renewTarget !== null}
        subscription={renewTarget}
        onClose={() => setRenewTarget(null)}
        onSuccess={() => refetch()}
      />

      {/* Create Subscription Modal */}
      <CreateSubscriptionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => refetch()}
      />
    </DashboardLayout>
  );
};
